const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/soh/eCheckin/sections/Versicherung/Blocks/InsuranceDetails.tsx';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import textMap from 'App/config/soh/eCheckin/translations';
import * as React from 'react';
import { forwardRef } from 'react';
import { getInsurancePaths } from '../Versicherung';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import { CIS4mNumberField } from '@cistec/common_web/modules/CIS4m/components/CIS4mNumberField';
import { VersicherungsdeckungField } from 'App/components/eCheckin/components/fields/metakat/VersicherungsdeckungField';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import { AHVField } from 'App/components/eCheckin/components/fields/AHVField';
import { fieldValueIsNot } from 'App/components/eCheckin/utils';
import { validateAHVNoDots } from '@cistec/common_web/components/Forms/utils/validation';

type Props = {
    path: string;
    hidden?: boolean;
};

export const InsuranceDetailsBlock = forwardRef((props: Props, ref: any) => {
    const formData = useCIS4mData();
    const hidden = props.hidden ?? false;

    const {
        PATH_INSURANCE_COVERAGE,
        PATH_INSURANCE_VVG,
        PATH_CARD_NUMBER,
        PATH_INSURANCE,
        PATH_INSURANCE_NUMBER,
        PATH_ADDITIONAL_INSURANCE,
        PATH_INSURANCE_NUMBER_ZV,
        PATH_AHV,
        PATH_NS_NUMBER,
        PATH_INSURANCE_INFORMATION,
    } = getInsurancePaths(props.path);

    // condition with PATH_INSURANCE_VVG
    const hideAdditionalInsuranceFields = fieldValueIsNot(formData, PATH_INSURANCE_VVG, ['0']);

    const hideAdditionalInsuranceVVGFields = fieldValueIsNot(formData, PATH_INSURANCE_COVERAGE, [
        'general-Switzerland',
        'semi-private',
        'private',
    ]);

    const hideAsylumFields = fieldValueIsNot(formData, PATH_INSURANCE_INFORMATION, ['asylsuchend']);
    const hideIvAndNotSwissRelatedFields = fieldValueIsNot(formData, PATH_INSURANCE_INFORMATION, [
        'iv',
        'inlandOhneChVersicherung',
    ]);
    const hideSwissInsuranceFields = fieldValueIsNot(formData, PATH_INSURANCE_INFORMATION, [
        'inlandMitChVersicherung',
        'auslandMitChVersicherung',
        'militärversicherung',
    ]);

    const insuranceDetailBlockRef = useCIS4m(ref);

    return (
        <CIS4mBlock ref={insuranceDetailBlockRef} hidden={hidden}>
            <AHVField
                label={textMap.sozialversicherungsnr}
                path={PATH_AHV}
                required={true}
                mask={'7569999999999'}
                checkValidity={validateAHVNoDots}
                hidden={hideIvAndNotSwissRelatedFields && hideSwissInsuranceFields}
            />
            <CIS4mNumberField
                label={textMap.nsNumber}
                path={PATH_NS_NUMBER}
                required={true}
                hidden={hideAsylumFields}
            />
            <CIS4mBlock hidden={hideSwissInsuranceFields}>
                <CIS4mTextField label={textMap.insurance} path={PATH_INSURANCE} required={true} />
                <CIS4mNumberField label={textMap.cardNumber} path={PATH_CARD_NUMBER} required={true} />
                <CIS4mNumberField label={textMap.insuranceNumber} path={PATH_INSURANCE_NUMBER} required={true} />
                <VersicherungsdeckungField
                    label={textMap.insuranceCoverage}
                    path={PATH_INSURANCE_COVERAGE}
                    required={true}
                />
                <CIS4mBlock hidden={hideAdditionalInsuranceVVGFields}>
                    <YesNoField path={PATH_INSURANCE_VVG} label={textMap.additionalInsuranceVVGText} required={true} />
                    <CIS4mBlock hidden={hideAdditionalInsuranceFields}>
                        <CIS4mTextField
                            label={textMap.additionalInsurance}
                            path={PATH_ADDITIONAL_INSURANCE}
                            required={true}
                        />
                        <CIS4mNumberField
                            label={textMap.insuranceNumberZV}
                            path={PATH_INSURANCE_NUMBER_ZV}
                            required={true}
                        />
                    </CIS4mBlock>
                </CIS4mBlock>
            </CIS4mBlock>
        </CIS4mBlock>
    );
});

InsuranceDetailsBlock.displayName = 'InsuranceDetailsBlock';
